/**
 * Auth Composable
 * 認證流程管理
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/auth/auth.js)
 * - 單例模式：使用 nuxtApp._authContext 避免 storeToRefs/computed/watch 重複創建
 */

import { storeToRefs } from 'pinia'
import urljoin from 'url-join'
import { ref, computed, watch, nextTick } from 'vue'

import { authApi, userApi } from '@/api'
import { useAuthStore, AUTH_STATE } from '@/stores/auth/auth'
import { useMainStore } from '@/stores/main'
import { storage } from '@/utils/storage'

export const useAuth = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 storeToRefs、computed 和 watch 只創建一次，避免 SSR 環境下記憶體洩漏
  if (!nuxtApp._authContext) {
    const authStore = useAuthStore()
    const mainStore = useMainStore()
    const { $request, $getDeviceToken, $bridge } = nuxtApp
    const { apiUrl, clientId } = useRuntimeConfig().public
    const { dtVerify } = useSite()
    const authToken = useAuthToken()
    const { getUserInfo, setUserInfo, fetchUserInfo: userInfoFetchUserInfo } = useUserInfo({ autoInit: false })
    const { token, isLoggedIn, setToken, extendToken, clearToken } = authToken
    const { reset } = useAuthOtp()
    const { setModal } = mainStore

    // 從 authStore 解構狀態 - 只調用一次
    const authStoreRefs = storeToRefs(authStore)
    const { authState, isPlaying, loginData, deviceTokenChecked } = authStoreRefs

    // SSR-safe: 將 pollingTimer 存儲在 nuxtApp context 而非 Pinia store
    // 這確保 timer ID 不會在 SSR 請求之間共享
    if (!nuxtApp._pollingTimer) {
      nuxtApp._pollingTimer = { value: null }
    }
    const pollingTimer = nuxtApp._pollingTimer

    if (!nuxtApp._pollFailureCount) {
      nuxtApp._pollFailureCount = { value: 0 }
    }
    const pollFailureCount = nuxtApp._pollFailureCount

    const POLL_LOGOUT_ERROR_CODES = ['ERR_A101', 'ERR_A201', 'ERR_A204', 'ERR_A103', 'ERR_A104', 'ERR_A108', 'ERR_F108', 'ERR_A113']
    const POLL_MAX_FAILURES = 5
    const POLL_MAX_FAILURES_BACKGROUND = 15

    // 保存 watch stop 函數，用於 cleanup 時停止所有 watch
    const stopWatchers = []

    // 保留在 composable 的本地狀態（業務邏輯相關）
    const tick = ref(2000)

    // computed - 只創建一次
    const isInline = computed(() => getUserInfo.value?.rank >= 0)
    const isNotInline = computed(() => !(getUserInfo.value?.rank >= 0))
    const isAuthenticated = computed(
      () =>
        authState.value === AUTH_STATE.AUTHENTICATED ||
        authState.value === AUTH_STATE.LOGIN_SUCCESS ||
        authState.value === AUTH_STATE.TOKEN_AUTHENTICATED ||
        authState.value === AUTH_STATE.INLINE ||
        authState.value === AUTH_STATE.LOGIN
    )

    const fetchUserInfo = async () => {
      try {
        const userData = await userInfoFetchUserInfo()

        if (nuxtApp.$sentry) {
          nuxtApp.$sentry.setUser({ id: userData.uid_site })
        }

        return userData
      } catch (error) {
        console.error('[useAuth] fetchUserInfo error:', error)
        throw error
      }
    }

    const checkDeviceToken = async () => {
      try {
        if (!dtVerify.value) {
          return
        }

        const { exist } = await $request.send(userApi.checkDt())
        if (!exist) {
          const dt = await $getDeviceToken()

          if (dt) {
            if (nuxtApp.$sentry) {
              nuxtApp.$sentry.setTag('dt', dt)
            }
            await $request.send(userApi.updateDt({ dt }))
          }
        }
      } catch (error) {
        console.error('[useAuth] checkDeviceToken error:', error)
        throw error
      }
    }

    const authenticate = async (data) => {
      try {
        const authData = { ...loginData.value, ...data }
        const response = await $request.send(authApi.login(authData))

        setToken(response.token)

        return response.token
      } catch (error) {
        console.error('[useAuth] authenticate error:', error)
        clearToken()
        authState.value = AUTH_STATE.IDLE
        throw error
      }
    }

    const authenticateApp = async (data) => {
      try {
        const response = await $request.send(authApi.loginApp(data))

        setToken(response.token)

        return response.token
      } catch (error) {
        console.error('[useAuth] authenticateApp error:', error)
        clearToken()
        authState.value = AUTH_STATE.IDLE
        throw error
      }
    }

    const stopPolling = () => {
      if (!import.meta.client) {
        return
      }

      if (pollingTimer.value) {
        clearInterval(pollingTimer.value)
        pollingTimer.value = null
      }

      if (nuxtApp._pollVisibilityHandler) {
        document.removeEventListener('visibilitychange', nuxtApp._pollVisibilityHandler)
        nuxtApp._pollVisibilityHandler = null
      }
    }

    const logout = async () => {
      try {
        if (token.value) {
          await $request.send(authApi.logout())
        }
      } catch (error) {
        console.error('[useAuth] logout error:', error)
      } finally {
        if ($bridge?.available) {
          $bridge.callHandler('resetAuth', null, () => {
            console.log('[Bridge] Auth Reset')
          })
        }
        reset()
        clearToken()
        setUserInfo({})
        authState.value = AUTH_STATE.IDLE
        deviceTokenChecked.value = false
        setModal(null)
        stopPolling()
      }
    }

    const startPolling = () => {
      if (!import.meta.client) {
        return
      }

      stopPolling()

      const pollInterval = tick.value * (isPlaying.value ? 5 : 1)

      pollFailureCount.value = 0

      // 背景→前景時重置失敗計數並立刻續期 token，避免背景期間累積的失敗 + 過期 token 直接觸發登出
      if (typeof document !== 'undefined') {
        const handleVisibilityVisible = () => {
          if (document.visibilityState !== 'visible') {
            return
          }
          pollFailureCount.value = 0
          if (isLoggedIn.value) {
            extendToken()
          }
        }
        nuxtApp._pollVisibilityHandler = handleVisibilityVisible
        document.addEventListener('visibilitychange', handleVisibilityVisible)
      }

      pollingTimer.value = setInterval(async () => {
        if (!isLoggedIn.value) {
          stopPolling()
          return
        }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          return
        }

        try {
          await fetchUserInfo()
          extendToken()
          pollFailureCount.value = 0
        } catch (error) {
          console.error('[useAuth] polling error:', error)

          if (POLL_LOGOUT_ERROR_CODES.includes(error?.code)) {
            stopPolling()
            await logout()
            return
          }

          pollFailureCount.value += 1
          const isBackground = typeof document !== 'undefined' && document.visibilityState !== 'visible'
          const maxFailures = isBackground ? POLL_MAX_FAILURES_BACKGROUND : POLL_MAX_FAILURES
          if (pollFailureCount.value >= maxFailures) {
            stopPolling()
            await logout()
          }
        }
      }, pollInterval)
    }

    const completeLoginFlow = async () => {
      authState.value = AUTH_STATE.AUTHENTICATED
      await fetchUserInfo()

      authState.value = AUTH_STATE.LOGIN_SUCCESS
      await nextTick()

      if (isInline.value) {
        authState.value = AUTH_STATE.INLINE
      } else {
        authState.value = AUTH_STATE.LOGIN
      }
      startPolling()
    }

    const loginWithToken = async (newToken) => {
      try {
        if (newToken) {
          setToken(newToken)
        }

        if (isLoggedIn.value) {
          authState.value = AUTH_STATE.TOKEN_AUTHENTICATED
          await fetchUserInfo()
          // extendToken()

          if (isInline.value) {
            authState.value = AUTH_STATE.INLINE
          } else {
            authState.value = AUTH_STATE.LOGIN
          }
          startPolling()
        }
      } catch (error) {
        console.error('[useAuth] loginWithToken error:', error)
        clearToken()
        authState.value = AUTH_STATE.IDLE
        throw error
      }
    }

    // watch - 只創建一次，保存 stop 函數供 cleanup 使用
    stopWatchers.push(
      watch(authState, async (newState) => {
        if (newState === AUTH_STATE.LOGIN && !deviceTokenChecked.value) {
          deviceTokenChecked.value = true
          await checkDeviceToken()
        }
      })
    )

    const loginWith = async (data) => {
      try {
        loginData.value = data
        authState.value = AUTH_STATE.AUTHENTICATING
        await authenticate()
        await completeLoginFlow()
      } catch (error) {
        console.error(`[useAuth] login (${data.provider}) error:`, error)
        throw error
      }
    }

    const loginWithId = async ({ username, password }) => loginWith({ provider: 'local', username, password })

    const loginWithPhone = async ({ countryCode: code, phone, password }) => loginWith({ provider: 'phone', countryCode: code, phone, password })

    const loginWithEmail = async ({ email, password }) => loginWith({ provider: 'email', email, password })

    const loginWithTelegram = async ({ loginType, additionalUserInfo: addInfo, isSignature, idToken }) => {
      // if (loginType == 'WebApp') {
      //   return loginWith({
      //     provider: 'telegram',
      //     loginType,
      //     additionalUserInfo: addInfo,
      //     isSignature,
      //   })
      // }

      const dt = dtVerify.value ? await $getDeviceToken() : null
      if (dt && nuxtApp.$sentry) {
        nuxtApp.$sentry.setTag('dt', dt)
      }

      const referralObj = {}
      const isFilled = (v) => ![undefined, 'undefined', null, 'null'].includes(v)

      const affiliateCode = storage.get('ac')
      const nickname = storage.get('nk')
      const tpPhone = storage.get('tp_phone')
      const tpCountryCode = storage.get('tp_countryCode')
      const tpEmail = storage.get('tp_email')

      if (isFilled(affiliateCode)) referralObj.ac = affiliateCode
      if (isFilled(nickname)) referralObj.nick_name = nickname
      if (isFilled(tpPhone)) referralObj.phone = tpPhone
      if (isFilled(tpCountryCode)) referralObj.countryCode = tpCountryCode
      if (isFilled(tpEmail)) referralObj.email = tpEmail

      storage.remove('tp_phone')
      storage.remove('tp_countryCode')
      storage.remove('tp_email')

      return loginWith({
        provider: 'telegram',
        loginType,
        additionalUserInfo: addInfo,
        isSignature,
        idToken,
        dt,
        platform: 'pwa',
        fc: storage.get('fc'),
        ...referralObj,
      })
    }

    const loginWithApp = async (data) => {
      try {
        authState.value = AUTH_STATE.AUTHENTICATING
        await authenticateApp(data)

        await completeLoginFlow()
      } catch (error) {
        console.error('[useAuth] loginWithApp error:', error)
        throw error
      }
    }

    const redirectTo3Party = async ({ provider, fc: friendCode }) => {
      try {
        const dt = dtVerify.value ? await $getDeviceToken() : null

        if (dt && nuxtApp.$sentry) {
          nuxtApp.$sentry.setTag('dt', dt)
        }

        const affiliateCode = storage.get('ac')
        const nickname = storage.get('nk')

        const messages = {
          url: window.location.href,
          affiliateCode,
          nickname,
        }

        if (nuxtApp.$sentry) {
          nuxtApp.$sentry.captureMessage(`[Auth] RedirectTo3Party Data: ${JSON.stringify(messages)}`, { level: 'info' })
        }

        const referralObj = {}
        if (![undefined, 'undefined', null, 'null'].includes(affiliateCode)) {
          referralObj.ac = affiliateCode
        }

        if (![undefined, 'undefined', null, 'null'].includes(nickname)) {
          referralObj.nick_name = nickname
        }

        window.location.href = urljoin(
          apiUrl,
          'v2',
          clientId,
          'login?' +
            new URLSearchParams({
              provider,
              redirectURI: `${window.location.protocol}//${window.location.host}`,
              ...referralObj,
              dt,
              platform: 'pwa',
              fc: friendCode,
            }).toString()
        )
      } catch (error) {
        console.error('[useAuth] redirectTo3Party error:', error)
        throw error
      }
    }

    const setPlay = (playing) => {
      isPlaying.value = playing
      if (pollingTimer.value) {
        startPolling()
      }
    }

    // cleanup 方法：停止所有 watch 和 polling，供 SSR cleanup plugin 調用
    const cleanup = () => {
      stopWatchers.forEach((stop) => stop?.())
      stopPolling()
    }

    nuxtApp._authContext = {
      authState,
      isPlaying,
      isInline,
      isNotInline,
      isAuthenticated,

      loginWithToken,
      loginWithId,
      loginWithPhone,
      loginWithEmail,
      loginWithTelegram,
      loginWithApp,
      redirectTo3Party,
      logout,
      setPlay,
      fetchUserInfo,
      stopPolling,
      cleanup,
    }
  }

  return nuxtApp._authContext
}
